import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Upload, FileText, Search, Check, X, Edit, Trash2, 
  AlertTriangle, RefreshCw, Download, Filter, ChevronDown,
  BookOpen, FileQuestion, Copy, Eye, Save, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const ContentManagement = ({ professions }) => {
  const [activeSection, setActiveSection] = useState('questions');
  const [selectedProfession, setSelectedProfession] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Questions state
  const [questions, setQuestions] = useState([]);
  const [questionsPage, setQuestionsPage] = useState(1);
  const [questionsTotalPages, setQuestionsTotalPages] = useState(1);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState(null);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  
  // Simulacros state
  const [simulacros, setSimulacros] = useState([]);
  const [simulacroName, setSimulacroName] = useState('');
  const [simulacroYear, setSimulacroYear] = useState('');
  
  // Flashcards review state
  const [flashcards, setFlashcards] = useState([]);
  const [flashcardsPage, setFlashcardsPage] = useState(1);
  const [flashcardsTotalPages, setFlashcardsTotalPages] = useState(1);
  const [editingFlashcard, setEditingFlashcard] = useState(null);
  
  useEffect(() => {
    if (selectedProfession) {
      loadTopics();
      loadStats();
    }
  }, [selectedProfession]);
  
  useEffect(() => {
    if (selectedProfession && activeSection === 'review') {
      loadQuestions();
    }
  }, [selectedProfession, selectedTopic, reviewFilter, sourceFilter, questionsPage, activeSection]);
  
  useEffect(() => {
    if (selectedProfession && activeSection === 'simulacros') {
      loadSimulacros();
    }
  }, [selectedProfession, activeSection]);
  
  useEffect(() => {
    if (selectedProfession && activeSection === 'review-flashcards') {
      loadFlashcardsForReview();
    }
  }, [selectedProfession, selectedTopic, flashcardsPage, activeSection]);
  
  const loadTopics = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/content/topics/${selectedProfession}`,
        { headers: getAuthHeaders() }
      );
      setTopics(response.data.topics || []);
    } catch (error) {
      toast.error('Error al cargar temas');
    }
  };
  
  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/content/stats/${selectedProfession}`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  
  const loadQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        profession_id: selectedProfession,
        status: reviewFilter,
        source: sourceFilter,
        page: questionsPage,
        limit: 20
      });
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      
      const response = await axios.get(
        `${API}/admin/content/questions/review?${params}`,
        { headers: getAuthHeaders() }
      );
      setQuestions(response.data.questions || []);
      setQuestionsTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Error al cargar preguntas');
    } finally {
      setLoading(false);
    }
  };
  
  const loadSimulacros = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/content/simulacros?profession_id=${selectedProfession}`,
        { headers: getAuthHeaders() }
      );
      setSimulacros(response.data.simulacros || []);
    } catch (error) {
      toast.error('Error al cargar simulacros');
    }
  };
  
  const loadFlashcardsForReview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        profession_id: selectedProfession,
        page: flashcardsPage,
        limit: 20
      });
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      
      const response = await axios.get(
        `${API}/admin/content/flashcards/review?${params}`,
        { headers: getAuthHeaders() }
      );
      setFlashcards(response.data.flashcards || []);
      setFlashcardsTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Error al cargar flashcards');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteFlashcard = async (flashcardId) => {
    if (!confirm('¿Eliminar esta flashcard?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/content/flashcards/${flashcardId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Flashcard eliminada');
      loadFlashcardsForReview();
      loadStats();
    } catch (error) {
      toast.error('Error al eliminar flashcard');
    }
  };
  
  const handleSaveFlashcard = async () => {
    if (!editingFlashcard) return;
    
    try {
      await axios.put(
        `${API}/admin/content/flashcards/${editingFlashcard.flashcard_id}`,
        {
          front: editingFlashcard.front,
          back: editingFlashcard.back,
          difficulty: editingFlashcard.difficulty
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Flashcard actualizada');
      setEditingFlashcard(null);
      loadFlashcardsForReview();
    } catch (error) {
      toast.error('Error al guardar flashcard');
    }
  };
  
  // ==================== UPLOAD HANDLERS ====================
  
  const handleUploadQuestions = async () => {
    if (!uploadFile || !selectedProfession || !selectedTopic) {
      toast.error('Selecciona profesión, tema y archivo');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('profession_id', selectedProfession);
      formData.append('topic_id', selectedTopic);
      formData.append('skip_duplicates', 'true');
      
      const response = await axios.post(
        `${API}/admin/content/questions/upload-csv`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResults(response.data);
      toast.success(`Importadas ${response.data.imported} preguntas`);
      loadStats();
      loadTopics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };
  
  const handleUploadFlashcards = async () => {
    if (!uploadFile || !selectedProfession || !selectedTopic) {
      toast.error('Selecciona profesión, tema y archivo');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('profession_id', selectedProfession);
      formData.append('topic_id', selectedTopic);
      formData.append('skip_duplicates', 'true');
      
      const response = await axios.post(
        `${API}/admin/content/flashcards/upload-csv`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResults(response.data);
      toast.success(`Importadas ${response.data.imported} flashcards`);
      loadStats();
      loadTopics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };
  
  const handleUploadSimulacro = async () => {
    if (!uploadFile || !selectedProfession || !simulacroName) {
      toast.error('Selecciona profesión, nombre y archivo');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('profession_id', selectedProfession);
      formData.append('name', simulacroName);
      if (simulacroYear) {
        formData.append('year', simulacroYear);
      }
      
      const response = await axios.post(
        `${API}/admin/content/simulacros/upload-csv`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`Simulacro creado con ${response.data.total_questions} preguntas`);
      setSimulacroName('');
      setSimulacroYear('');
      setUploadFile(null);
      loadSimulacros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear simulacro');
    } finally {
      setUploading(false);
    }
  };
  
  // ==================== REVIEW HANDLERS ====================
  
  const handleReviewQuestion = async (questionId, status) => {
    try {
      await axios.put(
        `${API}/admin/content/questions/${questionId}/review`,
        { status },
        { headers: getAuthHeaders() }
      );
      toast.success(`Pregunta ${status === 'approved' ? 'aprobada' : 'rechazada'}`);
      loadQuestions();
    } catch (error) {
      toast.error('Error al actualizar pregunta');
    }
  };
  
  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('¿Eliminar esta pregunta?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/content/questions/${questionId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Pregunta eliminada');
      loadQuestions();
      loadStats();
    } catch (error) {
      toast.error('Error al eliminar pregunta');
    }
  };
  
  const handleSaveQuestion = async () => {
    if (!editingQuestion) return;
    
    try {
      const formData = new FormData();
      formData.append('question_text', editingQuestion.question_text);
      formData.append('option_a', editingQuestion.options[0]?.option_text || '');
      formData.append('option_b', editingQuestion.options[1]?.option_text || '');
      formData.append('option_c', editingQuestion.options[2]?.option_text || '');
      formData.append('option_d', editingQuestion.options[3]?.option_text || '');
      const correctOpt = editingQuestion.options.find(o => o.is_correct);
      formData.append('correct_option', correctOpt?.option_letter || 'A');
      formData.append('explanation', editingQuestion.explanation || '');
      formData.append('difficulty', editingQuestion.difficulty || 'medium');
      
      await axios.put(
        `${API}/admin/content/questions/${editingQuestion.question_id}`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Pregunta actualizada');
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      toast.error('Error al guardar pregunta');
    }
  };
  
  // ==================== DUPLICATES HANDLERS ====================
  
  const handleScanDuplicates = async () => {
    if (!selectedProfession) {
      toast.error('Selecciona una profesión');
      return;
    }
    
    setScanningDuplicates(true);
    setDuplicates(null);
    
    try {
      const params = new URLSearchParams({ profession_id: selectedProfession });
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      
      const response = await axios.get(
        `${API}/admin/content/duplicates/scan?${params}`,
        { headers: getAuthHeaders() }
      );
      
      setDuplicates(response.data);
    } catch (error) {
      toast.error('Error al escanear duplicados');
    } finally {
      setScanningDuplicates(false);
    }
  };
  
  const handleRemoveDuplicates = async () => {
    if (!confirm('¿Eliminar todas las preguntas duplicadas? Se mantendrá la más reciente de cada grupo.')) {
      return;
    }
    
    try {
      const params = new URLSearchParams({ profession_id: selectedProfession });
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      
      const response = await axios.post(
        `${API}/admin/content/duplicates/remove?${params}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(response.data.message);
      setDuplicates(null);
      loadStats();
      loadTopics();
    } catch (error) {
      toast.error('Error al eliminar duplicados');
    }
  };
  
  const handleDeleteSimulacro = async (simulacroId) => {
    if (!confirm('¿Eliminar este simulacro?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/content/simulacros/${simulacroId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Simulacro eliminado');
      loadSimulacros();
    } catch (error) {
      toast.error('Error al eliminar simulacro');
    }
  };
  
  // ==================== RENDER ====================
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Gestión de Contenido</h2>
      </div>
      
      {/* Profession Selector */}
      <div className="bg-white rounded-xl border p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Seleccionar Profesión</label>
        <select
          value={selectedProfession}
          onChange={(e) => {
            setSelectedProfession(e.target.value);
            setSelectedTopic('');
          }}
          className="w-full max-w-md p-2 border rounded-lg"
        >
          <option value="">-- Seleccionar --</option>
          {professions.map(p => (
            <option key={p.profession_id} value={p.profession_id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      
      {selectedProfession && (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.total_questions}</div>
                <div className="text-sm text-slate-600">Preguntas</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.total_flashcards}</div>
                <div className="text-sm text-slate-600">Flashcards</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.total_simulacros}</div>
                <div className="text-sm text-slate-600">Simulacros</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">{stats.pending_review}</div>
                <div className="text-sm text-slate-600">Pendientes</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-slate-600">{stats.total_resources}</div>
                <div className="text-sm text-slate-600">Recursos</div>
              </div>
            </div>
          )}
          
          {/* Section Tabs */}
          <div className="flex flex-wrap gap-2 border-b pb-2">
            {[
              { id: 'questions', label: 'Subir Preguntas', icon: Upload },
              { id: 'flashcards', label: 'Subir Flashcards', icon: BookOpen },
              { id: 'simulacros', label: 'Simulacros', icon: FileText },
              { id: 'review', label: 'Revisar Preguntas', icon: Eye },
              { id: 'review-flashcards', label: 'Revisar Flashcards', icon: BookOpen },
              { id: 'duplicates', label: 'Duplicados', icon: Copy }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeSection === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveSection(tab.id)}
                className="flex items-center gap-2"
                size="sm"
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </Button>
            ))}
          </div>
          
          {/* Upload Questions Section */}
          {activeSection === 'questions' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <h3 className="text-lg font-semibold">Subir Preguntas desde CSV</h3>
              
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Formato del CSV:</p>
                <code className="text-xs bg-slate-200 p-2 rounded block">
                  question_text,option_a,option_b,option_c,option_d,correct_option,explanation,difficulty
                </code>
                <p className="mt-2 text-slate-600">
                  • correct_option: A, B, C o D<br/>
                  • difficulty: easy, medium o hard
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tema</label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">-- Seleccionar tema --</option>
                    {topics.map(t => (
                      <option key={t.topic_id} value={t.topic_id}>
                        {t.topic_number ? `Tema ${t.topic_number}: ` : ''}{t.name} ({t.question_count} Q)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Archivo CSV</label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>
              </div>
              
              <Button
                onClick={handleUploadQuestions}
                disabled={uploading || !uploadFile || !selectedTopic}
                className="w-full md:w-auto"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Preguntas
              </Button>
              
              {uploadResults && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Resultados:</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-green-600">{uploadResults.imported}</div>
                      <div className="text-sm">Importadas</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-amber-600">{uploadResults.duplicates}</div>
                      <div className="text-sm">Duplicadas</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-red-600">{uploadResults.errors?.length || 0}</div>
                      <div className="text-sm">Errores</div>
                    </div>
                  </div>
                  {uploadResults.errors?.length > 0 && (
                    <div className="mt-4 text-sm text-red-600">
                      <p className="font-medium">Errores:</p>
                      <ul className="list-disc list-inside">
                        {uploadResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Upload Flashcards Section */}
          {activeSection === 'flashcards' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <h3 className="text-lg font-semibold">Subir Flashcards desde CSV</h3>
              
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Formato del CSV:</p>
                <code className="text-xs bg-slate-200 p-2 rounded block">
                  front,back,difficulty,tags
                </code>
                <p className="mt-2 text-slate-600">
                  • difficulty: easy, medium o hard<br/>
                  • tags: separados por coma (opcional)
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tema</label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">-- Seleccionar tema --</option>
                    {topics.map(t => (
                      <option key={t.topic_id} value={t.topic_id}>
                        {t.topic_number ? `Tema ${t.topic_number}: ` : ''}{t.name} ({t.flashcard_count} FC)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Archivo CSV</label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>
              </div>
              
              <Button
                onClick={handleUploadFlashcards}
                disabled={uploading || !uploadFile || !selectedTopic}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Flashcards
              </Button>
              
              {uploadResults && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-green-600">{uploadResults.imported}</div>
                      <div className="text-sm">Importadas</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-amber-600">{uploadResults.duplicates}</div>
                      <div className="text-sm">Duplicadas</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-red-600">{uploadResults.errors?.length || 0}</div>
                      <div className="text-sm">Errores</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Simulacros Section */}
          {activeSection === 'simulacros' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <h3 className="text-lg font-semibold">Subir Simulacro desde CSV</h3>
                
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <p className="font-medium mb-2">Formato del CSV:</p>
                  <code className="text-xs bg-slate-200 p-2 rounded block">
                    question_text,option_a,option_b,option_c,option_d,correct_option,explanation
                  </code>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre del Simulacro</label>
                    <Input
                      value={simulacroName}
                      onChange={(e) => setSimulacroName(e.target.value)}
                      placeholder="Ej: Examen OPE 2023"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Año (opcional)</label>
                    <Input
                      type="number"
                      value={simulacroYear}
                      onChange={(e) => setSimulacroYear(e.target.value)}
                      placeholder="2023"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Archivo CSV</label>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleUploadSimulacro}
                  disabled={uploading || !uploadFile || !simulacroName}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Crear Simulacro
                </Button>
              </div>
              
              {/* Simulacros List */}
              <div className="bg-white rounded-xl border p-6">
                <h3 className="text-lg font-semibold mb-4">Simulacros Existentes</h3>
                {simulacros.length === 0 ? (
                  <p className="text-slate-500">No hay simulacros creados</p>
                ) : (
                  <div className="space-y-2">
                    {simulacros.map(sim => (
                      <div key={sim.simulacro_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <div className="font-medium">{sim.name}</div>
                          <div className="text-sm text-slate-500">
                            {sim.total_questions} preguntas • {sim.year || 'Sin año'}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSimulacro(sim.simulacro_id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Review Section */}
          {activeSection === 'review' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <h3 className="text-lg font-semibold">Revisar Preguntas</h3>
                
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="p-2 border rounded-lg text-sm"
                >
                  <option value="">Todos los temas</option>
                  {topics.map(t => (
                    <option key={t.topic_id} value={t.topic_id}>
                      Tema {t.topic_number}: {t.name?.substring(0, 30)}
                    </option>
                  ))}
                </select>
                
                <select
                  value={reviewFilter}
                  onChange={(e) => setReviewFilter(e.target.value)}
                  className="p-2 border rounded-lg text-sm"
                >
                  <option value="all">Todos los estados</option>
                  <option value="pending">Pendientes</option>
                  <option value="approved">Aprobadas</option>
                  <option value="rejected">Rechazadas</option>
                </select>
                
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="p-2 border rounded-lg text-sm"
                >
                  <option value="all">Todas las fuentes</option>
                  <option value="AI">Generadas por IA</option>
                  <option value="CSV-UPLOAD">Subidas por CSV</option>
                </select>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                </div>
              ) : questions.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay preguntas para mostrar</p>
              ) : (
                <div className="space-y-4">
                  {questions.map((q, idx) => (
                    <div key={q.question_id} className="border rounded-lg p-4">
                      {editingQuestion?.question_id === q.question_id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <textarea
                            value={editingQuestion.question_text}
                            onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})}
                            className="w-full p-2 border rounded-lg"
                            rows={3}
                          />
                          {editingQuestion.options.map((opt, optIdx) => (
                            <div key={optIdx} className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={opt.is_correct}
                                onChange={() => {
                                  const newOptions = editingQuestion.options.map((o, i) => ({
                                    ...o,
                                    is_correct: i === optIdx
                                  }));
                                  setEditingQuestion({...editingQuestion, options: newOptions});
                                }}
                              />
                              <span className="font-medium">{opt.option_letter}.</span>
                              <Input
                                value={opt.option_text}
                                onChange={(e) => {
                                  const newOptions = [...editingQuestion.options];
                                  newOptions[optIdx] = {...newOptions[optIdx], option_text: e.target.value};
                                  setEditingQuestion({...editingQuestion, options: newOptions});
                                }}
                                className="flex-1"
                              />
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Button onClick={handleSaveQuestion} size="sm">
                              <Save className="w-4 h-4 mr-1" /> Guardar
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setEditingQuestion(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-medium">{q.question_text}</p>
                              <div className="text-xs text-slate-500 mt-1">
                                {q.source} • {q.difficulty} • {q.review_status || 'pending'}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingQuestion(q)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReviewQuestion(q.question_id, 'approved')}
                                className="text-green-600"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReviewQuestion(q.question_id, 'rejected')}
                                className="text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteQuestion(q.question_id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {(q.options || []).map(opt => (
                              <div
                                key={opt.option_letter}
                                className={`p-2 rounded ${opt.is_correct ? 'bg-green-100' : 'bg-slate-50'}`}
                              >
                                <span className="font-medium">{opt.option_letter}.</span> {opt.option_text}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={questionsPage <= 1}
                      onClick={() => setQuestionsPage(p => p - 1)}
                    >
                      Anterior
                    </Button>
                    <span className="px-4 py-2 text-sm">
                      Página {questionsPage} de {questionsTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={questionsPage >= questionsTotalPages}
                      onClick={() => setQuestionsPage(p => p + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Review Flashcards Section */}
          {activeSection === 'review-flashcards' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <h3 className="text-lg font-semibold">Revisar Flashcards</h3>
                
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="p-2 border rounded-lg text-sm"
                >
                  <option value="">Todos los temas</option>
                  {topics.map(t => (
                    <option key={t.topic_id} value={t.topic_id}>
                      Tema {t.topic_number}: {t.name?.substring(0, 30)}
                    </option>
                  ))}
                </select>
                
                <Button variant="outline" size="sm" onClick={loadFlashcardsForReview}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
                </Button>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                </div>
              ) : flashcards.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay flashcards para mostrar</p>
              ) : (
                <div className="space-y-4">
                  {flashcards.map((fc, idx) => (
                    <div key={fc.flashcard_id} className="border rounded-lg p-4">
                      {editingFlashcard?.flashcard_id === fc.flashcard_id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-slate-700">Cara A (Pregunta)</label>
                            <textarea
                              value={editingFlashcard.front}
                              onChange={(e) => setEditingFlashcard({...editingFlashcard, front: e.target.value})}
                              className="w-full p-2 border rounded-lg mt-1"
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700">Cara B (Respuesta)</label>
                            <textarea
                              value={editingFlashcard.back}
                              onChange={(e) => setEditingFlashcard({...editingFlashcard, back: e.target.value})}
                              className="w-full p-2 border rounded-lg mt-1"
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700">Dificultad</label>
                            <select
                              value={editingFlashcard.difficulty}
                              onChange={(e) => setEditingFlashcard({...editingFlashcard, difficulty: e.target.value})}
                              className="w-full p-2 border rounded-lg mt-1"
                            >
                              <option value="easy">Fácil</option>
                              <option value="medium">Media</option>
                              <option value="hard">Difícil</option>
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={handleSaveFlashcard} size="sm">
                              <Save className="w-4 h-4 mr-1" /> Guardar
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setEditingFlashcard(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  fc.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                  fc.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {fc.difficulty || 'medium'}
                                </span>
                                <span className="text-xs text-slate-500">{fc.topic_name || fc.topic_id}</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingFlashcard({...fc})}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteFlashcard(fc.flashcard_id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-blue-600 mb-1">CARA A (Pregunta)</p>
                              <p className="text-sm">{fc.front}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-green-600 mb-1">CARA B (Respuesta)</p>
                              <p className="text-sm">{fc.back}</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={flashcardsPage <= 1}
                      onClick={() => setFlashcardsPage(p => p - 1)}
                    >
                      Anterior
                    </Button>
                    <span className="px-4 py-2 text-sm">
                      Página {flashcardsPage} de {flashcardsTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={flashcardsPage >= flashcardsTotalPages}
                      onClick={() => setFlashcardsPage(p => p + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Duplicates Section */}
          {activeSection === 'duplicates' && (
            <div className="bg-white rounded-xl border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Detectar Duplicados</h3>
                <div className="flex gap-2">
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="p-2 border rounded-lg text-sm"
                  >
                    <option value="">Toda la profesión</option>
                    {topics.map(t => (
                      <option key={t.topic_id} value={t.topic_id}>
                        Tema {t.topic_number}
                      </option>
                    ))}
                  </select>
                  <Button onClick={handleScanDuplicates} disabled={scanningDuplicates}>
                    {scanningDuplicates ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Escanear
                  </Button>
                </div>
              </div>
              
              {duplicates && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-2xl font-bold">{duplicates.total_questions}</div>
                      <div className="text-sm text-slate-500">Total preguntas</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-amber-600">{duplicates.duplicate_groups}</div>
                      <div className="text-sm text-slate-500">Grupos duplicados</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-red-600">{duplicates.total_duplicates}</div>
                      <div className="text-sm text-slate-500">Preguntas a eliminar</div>
                    </div>
                  </div>
                  
                  {duplicates.total_duplicates > 0 && (
                    <>
                      <Button
                        onClick={handleRemoveDuplicates}
                        variant="destructive"
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar {duplicates.total_duplicates} duplicados
                      </Button>
                      
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {duplicates.duplicates.map((group, idx) => (
                          <div key={idx} className="border rounded-lg p-3">
                            <div className="font-medium text-sm text-amber-600 mb-2">
                              {group.count} preguntas idénticas
                            </div>
                            {group.questions.map((q, qIdx) => (
                              <div key={qIdx} className="text-sm text-slate-600 truncate">
                                • {q.question_text}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {duplicates.total_duplicates === 0 && (
                    <div className="text-center py-8 text-green-600">
                      <Check className="w-12 h-12 mx-auto mb-2" />
                      <p>No se encontraron duplicados</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContentManagement;
